// Сторінка логіну для веб-версії (поза Telegram Mini App).
// Три способи входу:
//   1. Google Sign-In (1 тап) — основний
//   2. Пошта + одноразовий код (OTP) — універсальний fallback
//   3. Telegram Login Widget — для тих, у кого є акаунт у боті
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, api, ApiError, type LoginWidgetData } from '../lib/api';
import { setToken } from '../lib/auth';
import { isNativeApp } from '../lib/push';
import { IconTile } from '../components/AppIcons';
import { Capacitor } from '@capacitor/core';

// Людське повідомлення з будь-якої помилки — для діагностики показуємо як є
function errText(e: unknown, fallback: string): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error && e.message) return `${fallback} (${e.message})`;
  return fallback;
}

const BOT_USERNAME = import.meta.env.VITE_BOT_USERNAME ?? '';
// ПУБЛІЧНИЙ Google OAuth Client ID (той самий що в бекенді для перевірки токена).
const GOOGLE_CLIENT_ID = '926651756768-509q1ipojjoui06vm974ejfid1agkuv3.apps.googleusercontent.com';

// Мінімальні типи Google Identity Services (підвантажується скриптом).
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (cfg: {
            client_id: string;
            callback: (resp: { credential: string }) => void;
          }) => void;
          renderButton: (el: HTMLElement, opts: Record<string, unknown>) => void;
        };
      };
    };
  }
}

function Divider() {
  return (
    <div className="flex w-full max-w-sm items-center gap-3 text-xs text-tg-hint">
      <div className="h-px flex-1 bg-tg-secondary-bg" />
      або
      <div className="h-px flex-1 bg-tg-secondary-bg" />
    </div>
  );
}

export function Login() {
  const navigate = useNavigate();
  // У нативній апці (Capacitor WebView) Google Identity Services не працює —
  // Google блокує OAuth у вбудованих WebView. Показуємо тільки пошту й Telegram.
  const native = isNativeApp();

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 p-6">
      <div className="flex flex-col items-center text-center">
        <IconTile kind="signs" className="mb-3 h-16 w-16 rounded-2xl" glyphClass="h-9 w-9" />
        <h1 className="text-2xl font-bold">Тести ПДР</h1>
        <p className="mt-2 text-tg-hint">Увійди, щоб почати навчання</p>
      </div>

      {native && <ConnectionCheck />}

      {native && Capacitor.isPluginAvailable('SocialLogin') && (
        <>
          <NativeGoogleLogin onLoggedIn={() => navigate('/', { replace: true })} />
          <Divider />
        </>
      )}

      {!native && (
        <>
          <GoogleLogin onLoggedIn={() => navigate('/', { replace: true })} />
          <Divider />
        </>
      )}

      <EmailLogin onLoggedIn={() => navigate('/', { replace: true })} />

      {BOT_USERNAME && !native && (
        <>
          <Divider />
          <TelegramLogin onLoggedIn={() => navigate('/', { replace: true })} />
        </>
      )}

      {/* Нативна апка: веб-віджет Telegram у WebView не працює (попап) —
          натомість вхід одноразовим кодом, який видає бот. Так юзер із
          Premium, купленим у боті, входить тим самим акаунтом. */}
      {BOT_USERNAME && native && (
        <>
          <Divider />
          <TelegramCodeLogin onLoggedIn={() => navigate('/', { replace: true })} />
        </>
      )}

      <p className="max-w-sm text-center text-xs text-tg-hint">
        Відкривши бота в Telegram — навчання працює без логіну
      </p>
    </div>
  );
}

// Самоперевірка зв'язку із сервером (тільки нативна апка) — діагностика проблем
// входу: одразу видно, чи доходять запити до API і яка саме помилка.
function ConnectionCheck() {
  const [state, setState] = useState<string>('Перевіряю зв\'язок…');
  const [ok, setOk] = useState<boolean | null>(null);

  useEffect(() => {
    api
      .links()
      .then(() => {
        setOk(true);
        setState('З\'єднання із сервером: ок');
      })
      .catch((e) => {
        setOk(false);
        setState(`Немає зв'язку з сервером: ${(e as Error)?.message ?? 'невідома помилка'}`);
      });
  }, []);

  if (ok === true) return null; // все добре — нічого не показуємо
  return (
    <div
      className={`w-full max-w-sm rounded-lg px-3 py-2 text-center text-xs ${
        ok === false ? 'bg-red-500/10 text-red-400' : 'text-tg-hint'
      }`}
    >
      {state}
    </div>
  );
}

// ============================================================================
// Вхід через Google (Google Identity Services)
// ============================================================================
function GoogleLogin({ onLoggedIn }: { onLoggedIn: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handle = async (resp: { credential: string }) => {
      setError(null);
      try {
        const res = await auth.verifyGoogle(resp.credential);
        setToken(res.token);
        onLoggedIn();
      } catch (e) {
        setError(errText(e, 'Не вдалося увійти через Google'));
      }
    };
    const init = () => {
      const g = window.google;
      if (!g || !ref.current) return;
      g.accounts.id.initialize({ client_id: GOOGLE_CLIENT_ID, callback: handle });
      g.accounts.id.renderButton(ref.current, {
        type: 'standard',
        theme: 'filled_black',
        size: 'large',
        text: 'continue_with',
        shape: 'pill',
        logo_alignment: 'center',
        width: 320,
      });
    };
    if (window.google) {
      init();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.onload = init;
    document.body.appendChild(script);
  }, [onLoggedIn]);

  return (
    <div className="flex flex-col items-center gap-2">
      <div ref={ref} className="min-h-[44px]" />
      {error && <div className="text-xs text-tg-destructive">{error}</div>}
    </div>
  );
}

// ============================================================================
// Нативний вхід через Google (Capacitor-плагін) — для Android-апки, де веб-кнопка
// Google не працює (Google блокує OAuth у WebView). Плагін відкриває СИСТЕМНИЙ
// екран вибору акаунта й повертає idToken, який перевіряє наш /auth/google/verify.
// ============================================================================
function GoogleG() {
  return (
    <svg viewBox="0 0 48 48" className="h-5 w-5" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

function NativeGoogleLogin({ onLoggedIn }: { onLoggedIn: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function signIn() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const { SocialLogin } = await import('@capgo/capacitor-social-login');
      await SocialLogin.initialize({ google: { webClientId: GOOGLE_CLIENT_ID } });
      const { result } = await SocialLogin.login({ provider: 'google', options: {} });
      const idToken = (result as { idToken?: string | null }).idToken;
      if (!idToken) throw new Error('Google не повернув токен');
      const res = await auth.verifyGoogle(idToken);
      setToken(res.token);
      onLoggedIn();
    } catch (e) {
      // Користувач закрив вікно вибору акаунта — це не помилка
      const msg = String((e as Error)?.message ?? '');
      if (/cancel|dismiss|12501|user.?cancel/i.test(msg)) {
        setBusy(false);
        return;
      }
      setError(errText(e, 'Не вдалося увійти через Google'));
      setBusy(false);
    }
  }

  return (
    <div className="flex w-full max-w-sm flex-col items-center gap-2">
      <button
        onClick={signIn}
        disabled={busy}
        className="flex w-full items-center justify-center gap-3 rounded-full bg-white px-4 py-3 font-medium text-gray-800 shadow-sm transition active:scale-[0.99] disabled:opacity-60"
      >
        <GoogleG />
        {busy ? 'Вхід…' : 'Продовжити з Google'}
      </button>
      {error && <div className="text-xs text-tg-destructive">{error}</div>}
    </div>
  );
}

// ============================================================================
// Вхід через Telegram для НАТИВНОЇ апки — одноразовим кодом від бота.
// Кнопка відкриває бота (start=appcode) → бот шле код → юзер вводить тут.
// ============================================================================
function TelegramCodeLogin({ onLoggedIn }: { onLoggedIn: () => void }) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function verify() {
    if (busy || code.trim().length < 4) return;
    setBusy(true);
    setError(null);
    try {
      const res = await auth.verifyAppCode(code.trim().toUpperCase());
      setToken(res.token);
      onLoggedIn();
    } catch (e) {
      setError(errText(e, 'Код невірний або прострочений'));
      setBusy(false);
    }
  }

  return (
    <div className="card w-full max-w-sm space-y-3">
      <label className="block text-sm font-medium">Купував Premium у Telegram?</label>
      <p className="text-xs text-tg-hint">
        Увійди тим самим акаунтом: отримай код у боті й введи його тут — Premium
        підтягнеться автоматично.
      </p>
      <a
        href={`https://t.me/${BOT_USERNAME}?start=appcode`}
        target="_blank"
        rel="noopener noreferrer"
        className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#2f9bf0] px-4 py-2.5 text-sm font-semibold text-white"
      >
        🤖 Отримати код у боті
      </a>
      <input
        type="text"
        autoComplete="one-time-code"
        maxLength={8}
        value={code}
        onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
        onKeyDown={(e) => e.key === 'Enter' && verify()}
        placeholder="КОД З БОТА"
        className="w-full rounded-lg bg-tg-bg px-3 py-2.5 text-center font-mono text-lg tracking-[0.3em]"
      />
      <button
        onClick={verify}
        disabled={busy || code.trim().length < 4}
        className="btn-primary w-full disabled:opacity-50"
      >
        {busy ? 'Перевіряю…' : 'Увійти через Telegram'}
      </button>
      {error && <div className="text-xs text-tg-destructive">{error}</div>}
    </div>
  );
}

// ============================================================================
// Вхід через пошту (OTP)
// ============================================================================
function EmailLogin({ onLoggedIn }: { onLoggedIn: () => void }) {
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);

  // Таймер для повторної відправки
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  async function requestCode() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await auth.requestEmailCode(email.trim());
      if ('error' in res) {
        setError(res.message);
      } else {
        setStep('code');
        setCooldown(60);
      }
    } catch (e) {
      setError(errText(e, 'Не вдалося надіслати код'));
    } finally {
      setBusy(false);
    }
  }

  async function verify() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await auth.verifyEmailCode(email.trim(), code.trim());
      setToken(res.token);
      onLoggedIn();
    } catch (e) {
      setError(errText(e, 'Невірний код'));
      setBusy(false);
    }
  }

  return (
    <div className="card w-full max-w-sm space-y-3">
      {step === 'email' ? (
        <>
          <label className="block text-sm font-medium">Вхід через пошту</label>
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && email.includes('@') && requestCode()}
            placeholder="you@example.com"
            className="w-full rounded-lg bg-tg-bg px-3 py-2.5 text-sm"
          />
          <button
            onClick={requestCode}
            disabled={busy || !email.includes('@')}
            className="btn-primary w-full disabled:opacity-50"
          >
            {busy ? 'Надсилаю…' : 'Надіслати код'}
          </button>
        </>
      ) : (
        <>
          <label className="block text-sm font-medium">
            Код надіслано на <span className="text-tg-link">{email}</span>
          </label>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            onKeyDown={(e) => e.key === 'Enter' && code.length === 6 && verify()}
            placeholder="______"
            className="w-full rounded-lg bg-tg-bg px-3 py-2.5 text-center font-mono text-xl tracking-[0.5em]"
            autoFocus
          />
          <button
            onClick={verify}
            disabled={busy || code.length !== 6}
            className="btn-primary w-full disabled:opacity-50"
          >
            {busy ? 'Перевіряю…' : 'Увійти'}
          </button>
          <div className="flex items-center justify-between text-xs">
            <button
              onClick={() => {
                setStep('email');
                setCode('');
                setError(null);
              }}
              className="text-tg-hint hover:text-tg-text"
            >
              ← Інша пошта
            </button>
            <button
              onClick={requestCode}
              disabled={cooldown > 0 || busy}
              className="text-tg-link disabled:text-tg-hint"
            >
              {cooldown > 0 ? `Надіслати ще раз (${cooldown})` : 'Надіслати ще раз'}
            </button>
          </div>
        </>
      )}

      {error && <div className="text-xs text-tg-destructive">{error}</div>}
    </div>
  );
}

// ============================================================================
// Вхід через Telegram Login Widget (як було)
// ============================================================================
function TelegramLogin({ onLoggedIn }: { onLoggedIn: () => void }) {
  const widgetRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!BOT_USERNAME) return;
    (window as unknown as { onTelegramAuth: (u: LoginWidgetData) => void }).onTelegramAuth = async (
      data,
    ) => {
      setBusy(true);
      setError(null);
      try {
        const res = await auth.loginWithTelegram(data);
        setToken(res.token);
        onLoggedIn();
      } catch (e) {
        setError(errText(e, 'Не вдалося увійти'));
        setBusy(false);
      }
    };

    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.async = true;
    script.setAttribute('data-telegram-login', BOT_USERNAME);
    script.setAttribute('data-size', 'large');
    script.setAttribute('data-onauth', 'onTelegramAuth(user)');
    script.setAttribute('data-request-access', 'write');
    script.setAttribute('data-userpic', 'true');
    widgetRef.current?.appendChild(script);

    return () => {
      delete (window as unknown as { onTelegramAuth?: unknown }).onTelegramAuth;
    };
  }, [onLoggedIn]);

  return (
    <div className="flex flex-col items-center gap-2">
      <div ref={widgetRef} aria-busy={busy} />
      {busy && <p className="text-sm text-tg-hint">Авторизація…</p>}
      {error && <div className="text-xs text-tg-destructive">{error}</div>}
    </div>
  );
}
